import type { ReactNode } from "react";
import { cn } from "../cn";

export interface DetailLayoutProps {
  /** The main column: the facts or tabs the page is about. */
  readonly children: ReactNode;
  /**
   * The column beside the main one, sticky from `lg` up. Without it the page
   * is a single column, so a detail page without actions loses no width.
   */
  readonly rail?: ReactNode;
  /** Names the rail; it then renders as a labelled `aside` landmark. */
  readonly railLabel?: string;
  /**
   * Puts the rail above the main column below `lg`, for pages that are opened
   * to act rather than to read.
   */
  readonly railFirst?: boolean;
  readonly className?: string;
}

/**
 * The layout of a detail page: a main column with an optional sticky rail of
 * 20rem beside it.
 */
export function DetailLayout({
  children,
  rail,
  railLabel,
  railFirst = false,
  className,
}: DetailLayoutProps): ReactNode {
  const hasRail = rail !== undefined && rail !== null && rail !== false;
  const railClassName = cn(
    "flex flex-col gap-5 lg:sticky lg:top-20 lg:self-start",
    railFirst && "order-first lg:order-none",
  );

  return (
    <div
      className={cn(
        "grid gap-5",
        hasRail && "lg:grid-cols-[minmax(0,1fr)_20rem]",
        className,
      )}
    >
      <div className="flex min-w-0 flex-col gap-5">{children}</div>
      {hasRail &&
        (railLabel === undefined ? (
          <div className={railClassName}>{rail}</div>
        ) : (
          <aside aria-label={railLabel} className={railClassName}>
            {rail}
          </aside>
        ))}
    </div>
  );
}
